import { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CircleNotch, Lightning, Paperclip, FileText, X, ShieldCheck, Books, Sparkle, CalendarBlank, LockSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { PLAN_TYPES } from "@/lib/planiq";
import ThemeToggle from "@/components/ThemeToggle";
import Logomark from "@/components/Logomark";
import UserMenu from "@/components/UserMenu";
import LockNotice from "@/components/LockNotice";
import { useAuth } from "@/lib/auth";

const KICKER = "text-xs tracking-[0.25em] uppercase font-bold text-gray-500";
const inputCls =
  "rounded-none border-gray-300 text-lg h-14 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-1 focus-visible:border-gray-900";

const ACCEPT = ".pdf,.docx,.txt,.md,.csv,.json,.log";

export default function Ingestion() {
  const navigate = useNavigate();
  const location = useLocation();
  const presetType = PLAN_TYPES.some((t) => t.value === location.state?.planType)
    ? location.state.planType
    : "thirty_sixty_ninety";
  const [title, setTitle] = useState("");
  const [raw, setRaw] = useState("");
  const [planType, setPlanType] = useState(presetType);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const { user } = useAuth();
  const isFull = !!user && (user.role === "admin" || user.can_build);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const res = await api.parseDocuments(files);
      const parsed = res.documents || [];
      if (parsed.length === 0) {
        toast.error("Couldn't read any text from those files.");
      } else {
        setDocs((prev) => [...prev, ...parsed]);
        toast.success(`Attached ${parsed.length} document${parsed.length > 1 ? "s" : ""}.`);
      }
    } catch (e) {
      toast.error("Upload failed. Supported: PDF, DOCX, TXT, MD, CSV.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeDoc = (i) => setDocs((prev) => prev.filter((_, idx) => idx !== i));

  const start = async () => {
    if (!title.trim()) return toast.error("Give your initiative a title.");
    if (raw.trim().length < 20 && docs.length === 0)
      return toast.error("Add some context — type notes or attach a document.");
    setLoading(true);
    try {
      const plan = await api.createPlan(title.trim(), raw.trim(), docs, planType);
      navigate(`/plan/${plan._id}/blueprint`);
    } catch (e) {
      toast.error("Could not start. Please retry.");
      setLoading(false);
    }
  };

  const Header = () => (
    <header className="border-b border-gray-200 px-8 py-5 flex items-center justify-between">
      <div className="flex items-baseline gap-3">
        <button onClick={() => navigate(user ? "/library" : "/")} className="flex items-center gap-2 hover:opacity-70 transition-opacity">
          <Logomark size={24} />
          <span className="font-display font-black text-2xl tracking-tight">Xartis</span>
        </button>
        <span className={KICKER}>Plan anything</span>
      </div>
      <div className="flex items-center gap-3">
        <ThemeToggle />
        <UserMenu />
        <button onClick={() => navigate("/library")} data-testid="library-link"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-gray-300 hover:border-gray-900 transition-colors">
          <Books size={16} weight="duotone" /> My Plans
        </button>
      </div>
    </header>
  );

  if (user && !isFull) {
    return (
      <div className="min-h-screen flex flex-col" data-testid="ingestion-gate">
        <Header />
        <main className="flex-1 flex items-center justify-center px-8 py-16">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
            className="max-w-lg text-center space-y-6">
            <div className="mx-auto w-14 h-14 flex items-center justify-center border-2 border-amber-300 bg-amber-50">
              <LockSimple size={26} weight="duotone" className="text-amber-600" />
            </div>
            <div className="space-y-2">
              <p className={KICKER}>{user?.pass_expired ? "Pass expired" : "Free Taster"}</p>
              <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-none">
                Plan building is locked
              </h1>
              <p className="text-base text-gray-600 leading-relaxed">
                {user?.pass_expired
                  ? "Your Xartis Pass has expired. Your plans are safe in the Read-Only Vault — renew a pass to keep building, refining and exporting."
                  : "You've used the plan builds available on your account. Grab a Xartis Pass to keep building — your existing plans stay safe in your library."}
              </p>
            </div>
            <LockNotice reason="tier" className="text-left" />
            <div className="flex flex-wrap gap-3 justify-center pt-1">
              <button onClick={() => navigate("/pricing")} data-testid="gate-pricing-cta"
                className="inline-flex items-center gap-2 bg-gray-950 hover:bg-gray-700 text-white px-5 py-2.5 text-sm font-semibold transition-colors">
                <Sparkle size={15} weight="fill" className="text-amber-300" /> See passes
              </button>
              <button onClick={() => navigate("/library")} data-testid="gate-library-cta"
                className="inline-flex items-center gap-2 border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-800 hover:border-gray-900 transition-colors">
                <Books size={15} weight="duotone" /> My Plans
              </button>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 w-full max-w-3xl mx-auto px-8 py-16">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <p className={KICKER}>New Initiative</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none mt-2 mb-3">
            What are you planning?
          </h1>
          <p className="text-base text-gray-600 leading-relaxed mb-10 max-w-2xl">
            No questionnaire, no forms to fill. Pick a timeframe, drop in your context, and Xartis extracts the constraints
            and themes, then builds a plan tailored to your situation.
          </p>

          <div className="space-y-8">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800">Plan Timeframe</label>
              <div className="grid sm:grid-cols-3 gap-3" data-testid="plan-type-selector">
                {PLAN_TYPES.map((t) => {
                  const active = planType === t.value;
                  return (
                    <button
                      key={t.value}
                      data-testid={`plan-type-${t.value}`}
                      onClick={() => setPlanType(t.value)}
                      style={{
                        borderColor: t.accent,
                        backgroundColor: active ? `${t.accent}14` : undefined,
                        boxShadow: active ? `inset 0 0 0 1px ${t.accent}` : undefined,
                      }}
                      className="text-left border-2 p-4 transition-colors bg-white"
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <CalendarBlank size={18} weight="duotone" style={{ color: t.accent }} />
                        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border"
                          style={active
                            ? { backgroundColor: t.accent, borderColor: t.accent, color: "#fff" }
                            : { borderColor: t.accent, color: t.accent }}>
                          {t.tag}
                        </span>
                      </div>
                      <p className="font-display font-bold text-base tracking-tight"
                        style={{ color: active ? t.accent : undefined }}>{t.name}</p>
                      <p className="text-xs font-mono mt-0.5 text-gray-500">{t.span}</p>
                      <p className="text-xs mt-1.5 leading-snug text-gray-500">{t.blurb}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800">Plan Title</label>
              <Input
                data-testid="input-title"
                className={inputCls}
                placeholder="e.g. Improve Customer Onboarding Experience"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800">Let's talk Context...</label>
              <textarea
                data-testid="input-raw-context"
                rows={12}
                className="w-full resize-y border border-gray-300 rounded-none px-4 py-3 text-base leading-relaxed font-mono focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                placeholder="Drop in your context, background notes, goals, or messy bullet points here. Atlas — Xartis's in-house planning guru — will handle the heavy lifting, extracting the constraints, themes and focus areas for you."
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
              />
              <p className="text-xs text-gray-400 font-mono">{raw.trim().length} characters · markdown welcome</p>
              <div className="flex items-start gap-2 border-l-2 border-blue-700 bg-blue-50 px-3 py-2.5 mt-1" data-testid="ai-refine-hint">
                <Sparkle size={16} weight="fill" className="text-blue-700 shrink-0 mt-0.5" />
                <p className="text-xs text-gray-700 leading-relaxed">
                  <span className="font-semibold">Don't stress about getting this perfect.</span> Once your plan is built,
                  you can fine-tune any part of it in plain English with <span className="font-semibold">Atlas</span> —
                  Xartis's built-in planning assistant. Just ask, and Atlas will add actions, sharpen milestones, or
                  rebalance focus areas for you, without starting over.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800">Supporting Documents <span className="font-normal text-gray-400">(optional)</span></label>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept={ACCEPT}
                className="hidden"
                data-testid="file-input"
                onChange={(e) => handleFiles(e.target.files)}
              />
              <div
                data-testid="file-dropzone"
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
                className="border border-dashed border-gray-300 hover:border-gray-900 transition-colors cursor-pointer px-5 py-6 flex items-center gap-3 text-gray-500 hover:text-gray-900"
              >
                {uploading ? <CircleNotch size={20} className="animate-spin" /> : <Paperclip size={20} weight="duotone" />}
                <div className="text-sm">
                  <span className="font-semibold">{uploading ? "Parsing documents…" : "Attach files or drop them here"}</span>
                  <span className="block text-xs text-gray-400 font-mono">PDF, DOCX, TXT, MD, CSV · up to 10MB each</span>
                </div>
              </div>

              {docs.length > 0 && (
                <div className="space-y-1.5 pt-1" data-testid="attached-docs">
                  {docs.map((d, i) => (
                    <div key={`${d.filename}-${d.chars}-${i}`} data-testid={`attached-doc-${i}`}
                      className="flex items-center gap-2 border border-gray-200 bg-gray-50 px-3 py-2">
                      <FileText size={16} weight="duotone" className="text-gray-600 shrink-0" />
                      <span className="text-sm font-medium text-gray-800 truncate flex-1">{d.filename}</span>
                      <span className="text-xs font-mono text-gray-400 shrink-0">{d.chars.toLocaleString()} chars</span>
                      <button onClick={() => removeDoc(i)} data-testid={`remove-doc-${i}`}
                        className="text-gray-400 hover:text-red-600 shrink-0"><X size={15} /></button>
                    </div>
                  ))}
                </div>
              )}

              <p className="flex items-center gap-1.5 text-xs text-gray-500 pt-1">
                <ShieldCheck size={14} weight="duotone" className="text-emerald-700" />
                Documents are parsed for context and permanently deleted once your plan is built — nothing sensitive is stored.
              </p>
            </div>
          </div>

          <div className="flex justify-end mt-10 pt-6 border-t border-gray-200">
            <Button data-testid="analyze-context-button" disabled={loading} onClick={start}
              className="rounded-none bg-gray-950 hover:bg-gray-700 px-7 py-6 text-base">
              {loading ? (
                <><CircleNotch size={20} className="animate-spin" /> Reading your context…</>
              ) : (
                <><Lightning size={20} weight="fill" /> Analyze & Build Blueprint <ArrowRight size={18} /></>
              )}
            </Button>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
